import React, { useEffect, useState, useRef } from "react";
import { Ruler, Plus, Upload, Play, FileSpreadsheet, ArrowRightLeft, Trash2, Loader2, ChevronLeft, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const TO_CONF = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-red-100 text-red-700" };

export default function Takeoff() {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const load = async () => { const { data } = await api.get("/takeoffs"); setList(data); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load the takeoff list once on mount
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = window.prompt("Name this takeoff (e.g. 'Bath WTP — drawings')");
    if (!name) return;
    const { data } = await api.post("/takeoffs", { name });
    await load(); setActive(data.id);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this takeoff?")) return;
    await api.delete(`/takeoffs/${id}`); await load(); if (active === id) setActive(null);
  };

  if (active) return <TakeoffDetail id={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div className="p-5 sm:p-8 max-w-[1400px] mx-auto" data-testid="takeoff-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900 flex items-center gap-2.5">
            <Ruler className="h-7 w-7" /> Takeoff
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Run a standalone quantity takeoff from drawings/specs — no deal required. Published → vector (free · PyMuPDF) → estimate, with confidence. Export to Excel or convert to a bid.</p>
        </div>
        <button onClick={create} data-testid="new-takeoff-btn"
          className="shrink-0 inline-flex items-center gap-1.5 bg-zinc-900 text-white text-sm rounded-lg px-3.5 py-2 hover:bg-zinc-800">
          <Plus className="h-4 w-4" /> New takeoff
        </button>
      </div>

      <div className="mt-6 grid gap-3" data-testid="takeoff-list">
        {list === null && <div className="text-sm text-zinc-400">Loading…</div>}
        {list && list.length === 0 && (
          <div className="text-sm text-zinc-400 border border-dashed border-zinc-200 rounded-xl p-10 text-center">
            No takeoffs yet. Click <span className="font-medium text-zinc-600">New takeoff</span> to start.
          </div>
        )}
        {list && list.map((t) => (
          <div key={t.id} data-testid={`takeoff-card-${t.id}`}
            className="flex items-center justify-between gap-3 border border-zinc-200 rounded-xl px-4 py-3 hover:border-zinc-300 bg-white">
            <button onClick={() => setActive(t.id)} className="flex-1 text-left min-w-0">
              <div className="font-medium text-zinc-900 truncate">{t.name}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {t.document_count} doc(s) · <StatusPill status={t.status} />
                {t.converted_bid_id && <span className="text-emerald-600"> · converted to a bid</span>}
              </div>
            </button>
            <button onClick={() => remove(t.id)} data-testid={`delete-takeoff-${t.id}`} className="text-zinc-300 hover:text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const m = { empty: ["bg-zinc-100 text-zinc-500", "empty"], documents: ["bg-sky-100 text-sky-700", "ready to run"],
    running: ["bg-amber-100 text-amber-700", "running…"], ready: ["bg-emerald-100 text-emerald-700", "ready"],
    error: ["bg-red-100 text-red-700", "error"] }[status] || ["bg-zinc-100 text-zinc-500", status];
  return <span className={`px-1.5 py-0.5 rounded-sm ${m[0]}`}>{m[1]}</span>;
};

function TakeoffDetail({ id, onBack }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();
  const pollRef = useRef();

  const load = async () => { const { data } = await api.get(`/takeoffs/${id}`); setSt(data); return data; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the takeoff id changes
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [id]);

  useEffect(() => {
    if (st?.status === "running") {
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d.status !== "running") clearInterval(pollRef.current);
      }, 4000);
      return () => clearInterval(pollRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll only while status is running
  }, [st?.status]);

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/takeoffs/${id}/documents`, fd); }
      catch (er) { toast.error(`${f.name}: ${er?.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(false); e.target.value = ""; load();
  };
  const run = async () => {
    try { await api.post(`/takeoffs/${id}/run`); toast.success("Running takeoff…"); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Run failed."); }
  };
  const delDoc = async (docId) => { await api.delete(`/takeoffs/${id}/documents/${docId}`); load(); };
  const convert = async () => {
    if (!window.confirm("Create a new bid from this takeoff? Documents and quantities will be attached.")) return;
    try {
      const { data } = await api.post(`/takeoffs/${id}/convert`, {});
      toast.success(`Created bid ${data.est_code}.`);
      navigate(`/deals/${data.bid_id}`);
    } catch (er) { toast.error(er?.response?.data?.detail || "Convert failed."); }
  };

  if (!st) return <div className="p-8 text-sm text-zinc-400">Loading…</div>;
  const to = st.takeoff || {};
  const lines = to.lines || [];

  return (
    <div className="p-5 sm:p-8 max-w-[1400px] mx-auto" data-testid="takeoff-detail">
      <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3" data-testid="takeoff-back">
        <ChevronLeft className="h-4 w-4" /> All takeoffs
      </button>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h1 className="font-display font-bold text-2xl text-zinc-900 flex items-center gap-2"><Ruler className="h-6 w-6" /> {st.name}</h1>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple className="hidden" onChange={upload}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.dwg,.dxf,.docx,.xlsx" data-testid="takeoff-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="takeoff-upload-btn"
            className="text-sm inline-flex items-center gap-1.5 border border-zinc-300 rounded-lg px-3 py-1.5 hover:bg-zinc-50 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload docs
          </button>
          <button onClick={run} disabled={!(st.documents_count || (st.documents || []).length || to.lines) && !(st.document_count)} data-testid="takeoff-run-btn"
            className="text-sm inline-flex items-center gap-1.5 bg-zinc-900 text-white rounded-lg px-3 py-1.5 hover:bg-zinc-800">
            <Play className="h-4 w-4" /> Run takeoff
          </button>
          {to.lines && <a href={`${API}/takeoffs/${id}/takeoff.xlsx`} target="_blank" rel="noreferrer" data-testid="takeoff-export-btn"
            className="text-sm inline-flex items-center gap-1.5 border border-zinc-300 rounded-lg px-3 py-1.5 hover:bg-zinc-50">
            <FileSpreadsheet className="h-4 w-4" /> Excel</a>}
          {!st.converted_bid_id ? (
            <button onClick={convert} data-testid="takeoff-convert-btn"
              className="text-sm inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-lg px-3 py-1.5 hover:bg-emerald-100">
              <ArrowRightLeft className="h-4 w-4" /> Convert to bid</button>
          ) : (
            <button onClick={() => navigate(`/deals/${st.converted_bid_id}`)} className="text-sm inline-flex items-center gap-1.5 text-emerald-700">
              <ArrowRightLeft className="h-4 w-4" /> Open bid</button>
          )}
        </div>
      </div>
      <div className="text-[11px] text-zinc-500 mt-1"><StatusPill status={st.status} /></div>

      {/* Documents */}
      <div className="mt-5 border border-zinc-200 rounded-xl p-4" data-testid="takeoff-docs">
        <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">Documents</div>
        {(st.documents || []).length === 0 && <div className="text-xs text-zinc-400">Upload drawings/specs (PDF) to run a takeoff.</div>}
        <div className="grid gap-1.5">
          {(st.documents || []).map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-1.5 min-w-0 text-zinc-700"><FileText className="h-3.5 w-3.5 text-zinc-400 shrink-0" /> <span className="truncate">{d.original_filename}</span></span>
              <button onClick={() => delDoc(d.id)} className="text-zinc-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Takeoff result */}
      {st.status === "running" && <div className="mt-5 text-sm text-amber-600 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Running the cascade…</div>}
      {st.status === "error" && <div className="mt-5 text-sm text-red-600">Takeoff failed: {st.error}</div>}
      {to.lines && (
        <div className="mt-5" data-testid="takeoff-result">
          <div className="flex items-center gap-3 mb-3 text-[11px] flex-wrap">
            <span className="px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">{to.published_count || 0} published</span>
            <span className="px-2 py-0.5 rounded-sm bg-teal-100 text-teal-700">{to.measured_count || 0} measured</span>
            <span className="px-2 py-0.5 rounded-sm bg-sky-100 text-sky-700">{(to.computed_count || 0) + (to.estimated_count || 0)} estimated</span>
            <span className="px-2 py-0.5 rounded-sm bg-red-100 text-red-700">{to.needs_measurement_count || 0} need measurement</span>
            {(to.cad_count || 0) > 0 && <span className="px-2 py-0.5 rounded-sm bg-indigo-100 text-indigo-700" data-testid="takeoff-cad-count">{to.cad_count} CAD</span>}
            {(to.photo_count || 0) > 0 && <span className="px-2 py-0.5 rounded-sm bg-fuchsia-100 text-fuchsia-700" data-testid="takeoff-photo-count">{to.photo_count} photo</span>}
            {to.sdk_available && <span className="text-emerald-600">· Apryse measurement on</span>}
          </div>
          {(to.photo_scope?.length > 0) && (
            <div className="mb-3 border border-fuchsia-200 bg-fuchsia-50/50 rounded-md px-3 py-2" data-testid="takeoff-photo-scope">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700 mb-1">Photo scope evidence (vision — verify)</div>
              {to.photo_scope.map((s, i) => <div key={i} className="text-[11px] text-zinc-700">{s}</div>)}
            </div>
          )}
          {(to.flags || []).map((f, i) => (
            <div key={i} className="mb-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {f}
            </div>
          ))}
          <div className="grid gap-1.5 mt-2">
            {lines.map((ln, i) => (
              <div key={i} className={`border rounded-md px-3 py-2 ${ln.source === "needs-measurement" ? "border-amber-200 bg-amber-50/40" : "border-zinc-100"}`}>
                <div className="flex items-start gap-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-800">{ln.item}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{ln.basis || ln.status}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-mono text-zinc-900">{ln.quantity ?? "—"} <span className="text-zinc-400">{ln.unit}</span></div>
                    <div className="flex items-center gap-1 justify-end mt-0.5">
                      <span className="text-[10px] text-zinc-400">{ln.method}</span>
                      {ln.confidence && <span className={`text-[10px] px-1 rounded-sm ${TO_CONF[ln.confidence] || ""}`}>{ln.confidence}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {to.applicable === false && <div className="text-sm text-zinc-400 mt-2">No construction takeoff detected in these documents.</div>}
        </div>
      )}
    </div>
  );
}
